import type { ResourceMapRelationType } from "@k8s-dashboard/contracts";
import type { ResourceMapFilters } from "@/api/queries";

/**
 * Resource Map URL 상태 (ADR 0020, #8)
 * --------------------------------------------------------------------------
 * focus(res=group/version/resource, item=namespace/name/uid)·depth·relation을
 * URL에서만 읽어 **요청 인자로 파싱**합니다. 형식이 어긋나면 요청 0회로
 * fail-closed입니다 — 서버가 어차피 400으로 거절할 요청을 보내는 것 자체가
 * 소음입니다. 순수 함수·상수뿐이며 React·데이터 훅에 의존하지 않습니다.
 */

/* 서버 상한을 UI도 그대로 씁니다 — 넘는 값은 요청을 만들지 않고 여기서 끝냅니다. */
const MAX_COMPONENT_BYTES = 1024;
/* res/item 봉투 상한(UTF-16 code unit). 유효한 값은 성분 3개 + 구분자 2개를 넘을 수
   없고, code unit 수는 UTF-8 바이트 수 이하이므로 이보다 긴 원문은 인코딩해 볼
   필요조차 없습니다 — TextEncoder에 무제한 입력을 먹이지 않기 위한 선행 상한입니다. */
const MAX_FOCUS_PARAM_UNITS = MAX_COMPONENT_BYTES * 3 + 2;
/* depth 원문 길이 상한 — 정규식·Number 이전에 길이만 보고 끊습니다. */
const MAX_DEPTH_PARAM_UNITS = 3;
export const DEFAULT_DEPTH = 2;
export const MAX_DEPTH = 4;

/* 체크박스 표시·URL 기록 순서입니다. Record가 ResourceMapRelationType 전수를
   강제하므로 어휘가 바뀌면 컴파일이 깨집니다. queries의 RESOURCE_MAP_RELATIONS를
   import하지 않는 이유: 이 화면의 순서는 표시용일 뿐이고, 요청·캐시의 canonical
   정규화는 훅이 소유하므로 여기 순서가 어긋나도 wire·캐시는 canonical입니다.
   (기존 화면 테스트들이 queries 모듈을 통째로 mock하므로 값 import를 늘리지 않습니다.) */
const RELATION_ORDER_RANK: Record<ResourceMapRelationType, number> = {
  owner: 0,
  selects: 1,
  routesTo: 2,
  mounts: 3,
  scales: 4,
  protects: 5,
  storage: 6,
  serviceAccount: 7,
};
export const RELATION_ORDER: readonly ResourceMapRelationType[] = Object.freeze(
  (Object.keys(RELATION_ORDER_RANK) as ResourceMapRelationType[]).sort(
    (a, b) => RELATION_ORDER_RANK[a] - RELATION_ORDER_RANK[b],
  ),
);
const RELATION_SET = new Set<string>(RELATION_ORDER);

export type FocusParse =
  | { kind: "empty" }
  | { kind: "invalid"; reason: string }
  | {
      kind: "ok";
      filters: ResourceMapFilters;
      requestedDepth: number;
      /** UI 체크박스 상태 — 부분집합이거나, 필터 부재면 전체 선택입니다. */
      selected: ReadonlySet<ResourceMapRelationType>;
      subset: readonly ResourceMapRelationType[];
    };

/** focus가 없거나 무효일 때 훅에 넘기는 비활성 인자(요청 0회). */
export const EMPTY_FILTERS: ResourceMapFilters = {
  clusterId: "",
  group: "",
  version: "",
  resource: "",
  namespace: "",
  name: "",
  uid: "",
  depth: DEFAULT_DEPTH,
  relations: [],
};

const bytes = (value: string) => new TextEncoder().encode(value).length;
const validComponent = (value: string) => bytes(value) <= MAX_COMPONENT_BYTES && !value.includes("\0");

/**
 * URL 상태만으로 focus를 파싱합니다. 형식이 어긋나면 요청 0회로 fail-closed입니다 —
 * 서버가 어차피 400으로 거절할 요청을 보내는 것 자체가 소음입니다.
 */
export function parseFocus(params: URLSearchParams, clusterId: string): FocusParse {
  const res = params.get("res") ?? "";
  const item = params.get("item") ?? "";
  if (!res || !item) return { kind: "empty" };

  /* 봉투 길이(code unit)를 **인코딩·분해보다 먼저** 봅니다. */
  if (res.length > MAX_FOCUS_PARAM_UNITS || item.length > MAX_FOCUS_PARAM_UNITS) {
    return { kind: "invalid", reason: "식별자가 서버 상한(1024바이트)을 넘거나 허용되지 않는 문자를 담고 있습니다." };
  }

  const resParts = res.split("/");
  if (resParts.length !== 3 || resParts.some((p) => !p)) {
    return { kind: "invalid", reason: "리소스 선택(res) 형식이 올바르지 않습니다." };
  }
  const itemParts = item.split("/");
  if (itemParts.length !== 3) {
    return { kind: "invalid", reason: "항목 선택(item) 형식이 올바르지 않습니다." };
  }
  const [namespace, name, uid] = itemParts;
  if (!name || !uid) {
    return { kind: "invalid", reason: "focus의 이름과 UID가 모두 필요합니다." };
  }
  if (![...resParts, namespace, name, uid].every(validComponent)) {
    return { kind: "invalid", reason: "식별자가 서버 상한(1024바이트)을 넘거나 허용되지 않는 문자를 담고 있습니다." };
  }

  /* depth: 키가 없을 때만 기본 2입니다. 키가 있으면 비어 있지 않은 정수(1..4)
     정확히 하나여야 합니다 — 중복·빈 값·범위 밖은 조용히 고르지 않고 fail-closed입니다. */
  const depthValues = params.getAll("depth");
  let depth = DEFAULT_DEPTH;
  if (depthValues.length > 0) {
    if (depthValues.length !== 1 || depthValues[0].length > MAX_DEPTH_PARAM_UNITS || !/^\d+$/.test(depthValues[0])) {
      return { kind: "invalid", reason: "depth 값이 올바르지 않습니다. 1..4 정수 하나만 허용됩니다." };
    }
    const n = Number(depthValues[0]);
    if (n < 1 || n > MAX_DEPTH) {
      return { kind: "invalid", reason: "depth 값이 올바르지 않습니다. 1..4 정수 하나만 허용됩니다." };
    }
    depth = n;
  }

  /* 반복 relation: 빈 값·모르는 값·과잉 입력은 전부 fail-closed입니다.
     부재는 전체이고, 부분집합은 canonical 순서로 접힙니다. */
  const relationValues = params.getAll("relation");
  if (relationValues.length > RELATION_ORDER.length) {
    return { kind: "invalid", reason: "relation 필터가 어휘 크기를 넘었습니다." };
  }
  const chosen = new Set<ResourceMapRelationType>();
  for (const value of relationValues) {
    if (!RELATION_SET.has(value)) {
      return { kind: "invalid", reason: "relation 필터에 모르는 값이 있습니다." };
    }
    chosen.add(value as ResourceMapRelationType);
  }
  const subset: readonly ResourceMapRelationType[] =
    chosen.size === 0 || chosen.size === RELATION_ORDER.length
      ? []
      : RELATION_ORDER.filter((relation) => chosen.has(relation));
  const selected: ReadonlySet<ResourceMapRelationType> =
    subset.length === 0 ? new Set(RELATION_ORDER) : new Set(subset);

  return {
    kind: "ok",
    requestedDepth: depth,
    selected,
    subset,
    filters: {
      clusterId,
      group: resParts[0],
      version: resParts[1],
      resource: resParts[2],
      namespace,
      name,
      uid,
      depth,
      relations: subset,
    },
  };
}
